import React from "react";
import { Check, Star } from "lucide-react";
import BookingWidget from "./BookingWidget";
import GoogleProfileCard from "./GoogleProfileCard";
import { useLang } from "../i18n/LanguageContext";

const HERO_IMG = "/images/hero.webp";
const WHATSAPP =
  "https://wa.me/41791772424?text=" +
  encodeURIComponent("Hallo Zuerich Airport Taxi, ich möchte einen Flughafentransfer buchen.");

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function Hero() {
  const { t, lang } = useLang();
  const features = [t("badge_cancel"), t("badge_support"), t("badge_flight"), t("badge_meet")];
  const TAGLINE = {
    de: "Flughafentransfer ab 70 CHF",
    en: "Airport transfer from CHF 70",
    fr: "Transfert aéroport dès 70 CHF",
    it: "Transfer aeroportuale da 70 CHF",
    es: "Traslado al aeropuerto desde 70 CHF",
    pt: "Transfer do aeroporto a partir de 70 CHF",
    ru: "Трансфер из аэропорта от 70 CHF",
    ar: "نقل المطار ابتداءً من 70 CHF",
    fa: "ترانسفر فرودگاهی از 70 CHF",
    hi: "एयरपोर्ट ट्रांसफर 70 CHF से",
    zh: "机场接送 70 CHF 起",
    ko: "공항 트랜스퍼 70 CHF부터",
    ckb: "گواستنەوەی فڕۆکەخانە لە ٧٠ CHF ـەوە",
    tr: "Havalimanı transferi 70 CHF'den",
  };
  const tagline = TAGLINE[lang] || TAGLINE.de;
  const TITLE = {
    de: "Flughafen Zürich Taxi",
    en: "Airport Zurich Taxi",
    fr: "Taxi Aéroport de Zurich",
    it: "Taxi Aeroporto di Zurigo",
    es: "Taxi Aeropuerto de Zúrich",
    pt: "Táxi Aeroporto de Zurique",
    ru: "Такси аэропорт Цюрих",
    ar: "تاكسي مطار زيورخ",
    fa: "تاکسی فرودگاه زوریخ",
    hi: "एयरपोर्ट ज़्यूरिख टैक्सी",
    zh: "苏黎世机场出租车",
    ko: "취리히 공항 택시",
    ckb: "تاکسی فڕۆکەخانەی زوریخ",
    tr: "Zürih Havalimanı Taksi",
  };
  const title = TITLE[lang] || TITLE.de;
  return (
    <section id="home" className="relative hero-navy overflow-hidden">
      {/* Background image + navy overlay */}
      <img src={HERO_IMG} alt="Zuerich Airport Taxi Flughafentransfer Zürich" width="1264" height="848" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover object-[center_35%]" data-testid="hero-banner" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a1730]/45 via-[#0b1c3a]/40 to-[#0a1832]/80" />

      <div className="relative max-w-3xl mx-auto px-5 md:px-8 pt-28 md:pt-32 pb-16 md:pb-24">
        {/* Title on top, above the booking widget */}
        <div className="text-center mb-8 md:mb-10">
          <p className="text-xs md:text-sm font-semibold tracking-[0.28em] text-[#c9a227] uppercase" data-testid="hero-eyebrow">
            {t("hero_eyebrow")}
          </p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight" data-testid="hero-title">
            {title}
          </h1>
          <div className="mt-4 flex justify-center">
            <span className="inline-flex items-center rounded-full bg-black/40 backdrop-blur-md border border-[#F5B400]/50 px-4 py-1.5 font-display text-base md:text-xl font-bold text-[#F5B400] shadow-lg" data-testid="hero-price-tagline">
              {tagline}
            </span>
          </div>
        </div>

        {/* Booking widget card */}
        <BookingWidget />

        {/* Google profile / review card directly below the booking system */}
        <GoogleProfileCard />

        {/* Hero copy below */}
        <div className="mt-12 md:mt-14 text-center">
          <p className="text-base md:text-lg text-slate-300 max-w-xl mx-auto leading-relaxed" data-testid="hero-subtitle">
            {t("hero_subtitle")}
          </p>

          <div className="mt-8 flex justify-center">
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white font-semibold px-8 h-12 transition-colors">
              <WhatsAppIcon className="w-5 h-5" /> {t("hero_book_wa")}
            </a>
          </div>

          {/* Feature cards 2x2 */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-left" data-testid="hero-features">
            {features.map((b) => (
              <div key={b} className="flex items-center gap-3 rounded-2xl bg-white/[0.04] border border-white/10 px-5 py-4">
                <span className="grid place-items-center w-6 h-6 rounded-full bg-[#22c55e] shrink-0">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </span>
                <span className="text-sm font-medium text-white">{b}</span>
              </div>
            ))}
          </div>

          {/* Rating card */}
          <div className="mt-3.5 flex items-center gap-4 rounded-2xl bg-white/[0.04] border border-white/10 px-5 py-4 text-left">
            <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-4 h-4 fill-[#22c55e] text-[#22c55e]" />)}</div>
            <div>
              <p className="text-sm font-semibold text-white leading-tight" data-testid="hero-trust">{t("trust")}</p>
              <p className="text-xs text-slate-400 leading-tight">{t("hero_trust_sub")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
